import { useCallback, useEffect, useState } from "react";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saInput, saLabel } from "../../components/saShared";

const FEATURE_KEYS = [["campagne", "Campagne marketing"], ["whatsapp", "WhatsApp"], ["coupon", "Coupon"],
  ["giftcard", "Gift Card"], ["sondaggi", "Sondaggi"], ["automazioni", "Automazioni"], ["fidelity", "Fidelity"],
  ["coda", "Coda walk-in"], ["sms_wallet", "Wallet SMS"], ["centralino", "Centralino telefonico"]];

const EMPTY = { name: "", prezzo_mensile: 0, prezzo_annuale: 0, descrizione: "", funzionalita: "",
  feature_keys: [], limite_utenti: "", limite_campagne: "", limite_contatti: "", limite_messaggi: "" };

export default function SAPiani() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, form}

  const load = useCallback(() => {
    api.get("/superadmin/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const f = editing.form;
    const payload = {
      name: f.name, descrizione: f.descrizione,
      prezzo_mensile: parseFloat(f.prezzo_mensile) || 0,
      prezzo_annuale: parseFloat(f.prezzo_annuale) || 0,
      funzionalita: f.funzionalita.split("\n").map((s) => s.trim()).filter(Boolean),
      feature_keys: f.feature_keys || [],
      limite_utenti: f.limite_utenti === "" ? null : parseInt(f.limite_utenti),
      limite_campagne: f.limite_campagne === "" ? null : parseInt(f.limite_campagne),
      limite_contatti: f.limite_contatti === "" ? null : parseInt(f.limite_contatti),
      limite_messaggi: f.limite_messaggi === "" ? null : parseInt(f.limite_messaggi),
      attivo: editing.id ? undefined : true,
    };
    if (payload.attivo === undefined) delete payload.attivo;
    try {
      if (editing.id) await api.patch(`/superadmin/plans/${editing.id}`, payload);
      else await api.post("/superadmin/plans", payload);
      toast.success("Piano salvato");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (id) => {
    try { await api.post(`/superadmin/plans/${id}/toggle`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="sa-piani">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Piani</h1>
        <button onClick={() => setEditing({ form: EMPTY })} data-testid="sa-new-plan"
          className="h-11 px-4 rounded-md bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuovo piano
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`${saCard} p-5 ${!p.attivo ? "opacity-50" : ""}`} data-testid={`sa-plan-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="font-display font-bold text-lg text-white">{p.name}</div>
              <button onClick={() => toggle(p.id)} data-testid={`sa-plan-toggle-${p.id}`}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.attivo ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-zinc-700/30 text-zinc-500 border-zinc-600"}`}>
                {p.attivo ? "ATTIVO" : "DISATTIVO"}
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">{p.descrizione}</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-3xl font-black text-amber-400">€{p.prezzo_mensile}</span>
              <span className="text-xs text-zinc-500">/mese · €{p.prezzo_annuale}/anno</span>
            </div>
            <ul className="mt-3 space-y-1">
              {p.funzionalita.map((f) => <li key={f} className="text-xs text-zinc-300">· {f}</li>)}
            </ul>
            <div className="mt-3 pt-3 border-t border-zinc-800 text-[11px] text-zinc-500 space-y-0.5">
              <div>Utenti: {p.limite_utenti ?? "illimitati"} · Campagne: {p.limite_campagne ?? "∞"}</div>
              <div>Contatti: {p.limite_contatti ?? "∞"} · Messaggi: {p.limite_messaggi ?? "∞"}</div>
              <div>{p.restaurants_count} ristoranti su questo piano</div>
            </div>
            <button onClick={() => setEditing({ id: p.id, form: { ...p, funzionalita: p.funzionalita.join("\n"),
              feature_keys: p.feature_keys ?? [],
              limite_utenti: p.limite_utenti ?? "", limite_campagne: p.limite_campagne ?? "",
              limite_contatti: p.limite_contatti ?? "", limite_messaggi: p.limite_messaggi ?? "" } })}
              data-testid={`sa-plan-edit-${p.id}`}
              className="mt-3 w-full h-9 rounded-md bg-zinc-800 text-zinc-200 text-xs font-bold">Modifica</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setEditing(null)}>
          <form onSubmit={save} className={`${saCard} w-full sm:max-w-lg rounded-t-xl sm:rounded-xl p-5 space-y-3 max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-plan-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">{editing.id ? "Modifica piano" : "Nuovo piano"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div><label className={saLabel}>Nome *</label>
              <input required value={editing.form.name} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
                className={saInput} data-testid="plan-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={saLabel}>Prezzo mensile €</label>
                <input type="number" step="0.01" value={editing.form.prezzo_mensile} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, prezzo_mensile: e.target.value } })}
                  className={saInput} data-testid="plan-monthly" /></div>
              <div><label className={saLabel}>Prezzo annuale €</label>
                <input type="number" step="0.01" value={editing.form.prezzo_annuale} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, prezzo_annuale: e.target.value } })}
                  className={saInput} data-testid="plan-yearly" /></div>
            </div>
            <div><label className={saLabel}>Descrizione</label>
              <input value={editing.form.descrizione} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, descrizione: e.target.value } })}
                className={saInput} data-testid="plan-desc" /></div>
            <div><label className={saLabel}>Funzionalità (una per riga)</label>
              <textarea rows={4} value={editing.form.funzionalita} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, funzionalita: e.target.value } })}
                className="w-full px-3 py-2 rounded-md border border-zinc-700 bg-zinc-800 text-sm text-zinc-100" data-testid="plan-features" /></div>
            <div><label className={saLabel}>Moduli attivi nel piano (gating)</label>
              <div className="grid grid-cols-2 gap-1.5" data-testid="plan-feature-keys">
                {FEATURE_KEYS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-zinc-300">
                    <input type="checkbox" checked={(editing.form.feature_keys || []).includes(k)}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form,
                        feature_keys: e.target.checked
                          ? [...(editing.form.feature_keys || []), k]
                          : (editing.form.feature_keys || []).filter((x) => x !== k) } })}
                      data-testid={`plan-fk-${k}`} />
                    {l}
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">I moduli non spuntati mostrano al ristorante il lucchetto con invito all'upgrade.</p>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[["limite_utenti", "Utenti"], ["limite_campagne", "Campagne"], ["limite_contatti", "Contatti"], ["limite_messaggi", "Messaggi"]].map(([k, l]) => (
                <div key={k}><label className={saLabel}>{l}</label>
                  <input type="number" min="0" value={editing.form[k]} placeholder="∞"
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, [k]: e.target.value } })}
                    className={saInput} data-testid={`plan-${k}`} /></div>
              ))}
            </div>
            <button className="w-full h-12 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm" data-testid="plan-submit">Salva piano</button>
          </form>
        </div>
      )}
    </div>
  );
}
